import { Box, FormControlLabel, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import React, { useEffect } from "react";

import { DataGrid, ProgressModal } from "../../../components";
import { DataWrapperBox } from "../../../components/Boxes/DataWrapperBox/DataWrapperBox.style";
import HorizontalBox from "../../../components/Boxes/HorizontalBox/HorizontalBox";
import Button from "../../../components/Buttons/Button/Button";
import { DateRangePicker } from "../../../components/DateRangePicker/DateRangePicker";
import ConfirmationDialog from "../../../components/Modal/ConfirmationDialog";
import MultiSelectFilters from "../../../components/MultiSelectFilters/MultiSelectFilters";
import SingleSelect from "../../../components/SingleSelect/SingleSelect";
import Switch from "../../../components/Switch/Switch";
import { useScheduleOfInvestments } from "../../fund/scheduleOfInvestments/ScheduleOfInvestments.hooks";
import { ReportDetails } from "../reports/glReports/glReportDetails/ReportDetails";
import { BalanceSheetCustom } from "../reports/glReports/glReportList/BalanceSheetCustom";
import { IncomeStatementCustom } from "../reports/glReports/glReportList/IncomeStatementCustom";
import { FinancialStatementsActionsContainer, FinancialStatementsSelectorButtonBox, FsDataGridBox, MainContainer, NoDataMessageTypography, StyledDateFilterBox, StyledFilterBox, StyledFilterSpacerBox, StyledSwitchFilterBox } from "./FinancialStatements.style";
import { FsViews, useFinancialStatements } from "./FinancialStatementsMain.hooks";

export const GLFinancialStatements: React.FC = () => {

    const {
        isLoadingList,
        currSOIview,
        setCurrSOIview,
        handleSoiViewChange,
        soiGridData,
        handleColumnOrderChange,
        dateFilter: soiDateFilter,
        setDateFilter: setSOIDateFilter,
      } = useScheduleOfInvestments();

    const {
        attributeFilterDisabled,
        attributes,
        confirmFsNamechange,
        confirmNameChangeDialog,
        dateRange,
        editModeActivated,
        filteredAccounts,
        filteredAttributes,
        filteredLedgers,
        financialStatementsList,
        financialStatementsParams,
        financialStatementsView,
        fsDetailsApiParams,
        funds,
        fundsFilter,
        generalAttributes,
        handleAccountFilterChange,
        handleAttributeFilterChange,
        handleCloseReportDetails,
        handleDataGridChange,
        handleDateRangeChange,
        handleEditMode,
        handleFsNameChange,
        handleFundFilterChange,
        handleLedgerFilterChange,
        handleRoundedReport,
        handleRunFinancialStatementsButtonClick,
        handleStatusFilterChange,
        handleSuppressIfZeroChange,
        handleValueLinkButtonClick,
        handleViewSelection,
        invalidSelectedLedgers,
        isLoading,
        level1Clicked,
        netRow,
        noDataMessage,
        readonly,
        reportView,
        retainedRow,
        roundedReportActivated,
        runFinancialStatementsView,
        selectedAccounts,
        selectedAttributes,
        selectedFunds,
        selectedLedgers,
        selectedStatus,
        setConfirmNameChangeDialog,
        setIsLoading,
        setMainFSListData,
        setRoundedReportActivated,
        soiView,
        statusFilter,
        summaryRowClicked,
        suppressIfZero,
        triggerBuilder,
        validFilters,
        viewFiltersState,
        zeroSumStartBalance,
    } = useFinancialStatements(
        setCurrSOIview,
        handleSoiViewChange
        );

    let fsViewComponent;

    switch(runFinancialStatementsView) {
        case FsViews.unrealizedSOI:
            fsViewComponent =
                <DataGrid
                    id="soi_data_grid"
                    dataList={soiGridData.soiGridRows || []}
                    headerList={soiGridData.dataGridColDefs}
                    noDataMessage={isLoadingList ? "Fetching Data..." : undefined}
                    minHeight="80vh"
                    autoHeight={false}
                    activeHeaderFields={soiGridData.dataGridColDefs.length}
                    checkboxSelection={false}
                    onColumnOrderChange={(colDefs) => {
                    handleColumnOrderChange(colDefs.oldIndex, colDefs.targetIndex);
                    }}
                />;
            break;
        case FsViews.realizedSOI:
            fsViewComponent =
                <DataGrid
                    id="soi_data_grid"
                    dataList={soiGridData.soiGridRows || []}
                    headerList={soiGridData.dataGridColDefs}
                    noDataMessage={isLoadingList ? "Fetching Data..." : undefined}
                    minHeight="80vh"
                    autoHeight={false}
                    activeHeaderFields={soiGridData.dataGridColDefs.length}
                    checkboxSelection={false}
                    onColumnOrderChange={(colDefs) => {
                    handleColumnOrderChange(colDefs.oldIndex, colDefs.targetIndex);
                    }}
                />;
            break;
        case FsViews.balanceSheet:
            fsViewComponent =
                <BalanceSheetCustom 
                    params={financialStatementsParams!} 
                    setMainReportListData={setMainFSListData}
                    reportView={financialStatementsView}
                    suppressIfZero={suppressIfZero}
                    dateRange={dateRange}
                    funds={funds}
                    attributes={attributes}
                    viewFilters={viewFiltersState}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    handleValueLinkButtonClick={handleValueLinkButtonClick}
                    onDataGridChange={handleDataGridChange}
                    handleDateRangeChange={handleDateRangeChange}
                    fsView={true}
                    handleFsNameChange={handleFsNameChange}
                    triggerBuilder={triggerBuilder}
                    editModeActivated={editModeActivated}
                    roundedReportActivated={roundedReportActivated}
                    generalAttributes={generalAttributes}
                    setRoundedReportActivated={setRoundedReportActivated}
                />;
            break;
        case FsViews.incomeStatement:
            fsViewComponent =
                <IncomeStatementCustom 
                    params={financialStatementsParams!} 
                    setMainReportListData={setMainFSListData}
                    reportView={financialStatementsView}
                    suppressIfZero={suppressIfZero}
                    dateRange={dateRange}
                    funds={funds}
                    attributes={attributes}
                    viewFilters={viewFiltersState}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    handleValueLinkButtonClick={handleValueLinkButtonClick}
                    onDataGridChange={handleDataGridChange}
                    handleDateRangeChange={handleDateRangeChange}
                    fsView={true}
                    handleFsNameChange={handleFsNameChange}
                    triggerBuilder={triggerBuilder}
                    editModeActivated={editModeActivated}
                    roundedReportActivated={roundedReportActivated}
                    setRoundedReportActivated={setRoundedReportActivated}
                />;
            break;
        default:
            fsViewComponent = null;
    }

    return (
        <>
            <MainContainer id='financial_statements_main' role='main'>
                <HorizontalBox fullWidth noPadding>
                    <HorizontalBox noPadding>
                        <Typography variant='h1'>Financial Statements</Typography>
                        <FinancialStatementsSelectorButtonBox>
                            <SingleSelect
                                id='fs_selector_view'
                                value={financialStatementsView}
                                optionList={financialStatementsList}
                                label={'Select Financial Statement'}
                                handleOptionSelection={handleViewSelection}
                                width='300px'
                            />
                        {soiView ? (
                            <StyledDateFilterBox>
                            <DatePicker
                            label='Date'
                            renderInput={(params) => (
                                <TextField {...params} size="small" id="filter_date" />
                            )}
                            onChange={(value) => {setSOIDateFilter(value);}}
                            value={soiDateFilter}
                            />
                            </StyledDateFilterBox>
                        ) : ('')}
                        {reportView ? (
                            <StyledSwitchFilterBox>
                                { !readonly ? 
                                (<FormControlLabel
                                    id="chk_edit_mode"
                                    label="Edit Mode"
                                    control={
                                        <Switch
                                        id="switch_edit_mode"
                                        checked={editModeActivated}
                                        label="Edit Mode"
                                        onChange={!validFilters ? null : handleEditMode}
                                        />
                                    }
                                    disabled={!validFilters}
                                />) : ('')}
                                <FormControlLabel
                                    id="chk_rounded_report"
                                    label="Rounded Report"
                                    sx={{ marginLeft: '1px' }}
                                    control={
                                        <Switch
                                        id="switch_rounded_report"
                                        checked={roundedReportActivated}
                                        label="Rounded Report"
                                        onChange={!validFilters ? null : handleRoundedReport}
                                        />
                                    }
                                    disabled={!validFilters}
                                />
                            </StyledSwitchFilterBox>
                        ) : ('')}
                        </FinancialStatementsSelectorButtonBox>
                    </HorizontalBox>
                </HorizontalBox>
                <div/>
                <DataWrapperBox gapSize={0}>
                    { isLoading && <ProgressModal
                        id="gl_report_info_loading"
                        showProgress={isLoading}
                        />}
                    { isLoadingList &&
                    <ProgressModal
                        id='financial_statement_info_loading'
                        showProgress={isLoadingList}
                        />}
                    <HorizontalBox id='financial_statements_filter_box' fullWidth noPadding>
                        {!soiView ? (
                        <FinancialStatementsActionsContainer>
                            <StyledFilterBox>
                            <MultiSelectFilters
                                size="small"
                                id={"fund_filter"}
                                value={selectedFunds}
                                onFilter={handleFundFilterChange}
                                label={`Fund (${selectedFunds.length})`}
                                listData={fundsFilter as []}
                                filterName={"fund_filter"}
                            />
                            </StyledFilterBox>
                            <StyledFilterBox>
                            <MultiSelectFilters
                                size="small"
                                id={"ledger_filter"}
                                value={selectedLedgers}
                                onFilter={handleLedgerFilterChange}
                                label={`GL Name (${selectedLedgers.length})`}
                                listData={filteredLedgers as []}
                                filterName={"ledger_filter"}
                            />
                            </StyledFilterBox>
                            <StyledFilterBox>
                            <MultiSelectFilters
                                size="small"
                                id={"account_filter"}
                                value={selectedAccounts}
                                onFilter={handleAccountFilterChange}
                                label={`Account (${selectedAccounts.length}/${filteredAccounts.length})`}
                                listData={filteredAccounts as []}
                                filterName={"account_filter"}
                            />
                            </StyledFilterBox>
                            { attributeFilterDisabled ? ("") :
                            (<StyledFilterBox>
                            <MultiSelectFilters
                                size="small"
                                id={"attribute_filter"}
                                value={selectedAttributes}
                                onFilter={handleAttributeFilterChange}
                                label={`Account Attributes (${selectedAttributes.length})`}
                                listData={filteredAttributes as []}
                                filterName={"attribute_filter"}
                            />
                            </StyledFilterBox>)}
                            <StyledFilterBox>
                            <MultiSelectFilters
                                size="small"
                                id={"status_filter"}
                                value={selectedStatus}
                                onFilter={handleStatusFilterChange}
                                label={`Status (${selectedStatus.length})`}
                                listData={statusFilter as []}
                                filterName={"status_filter"}
                            />
                            </StyledFilterBox>
                            <StyledFilterSpacerBox/>
                            <StyledFilterBox>
                            <DateRangePicker 
                                disabled={false}
                                dateRange={dateRange} 
                                onChange={handleDateRangeChange}
                            />
                            </StyledFilterBox>
                            <StyledFilterBox>
                            <FormControlLabel
                                id="chk_supress_if_zero"
                                label="Suppress 0 Lines"
                                control={
                                    <Switch
                                    id="switch_supress_if_zero"
                                    checked={suppressIfZero}
                                    label="Suppress 0 Lines"
                                    onChange={!validFilters ? null : handleSuppressIfZeroChange}
                                    />
                                }
                                disabled={!validFilters}
                                />
                            </StyledFilterBox>
                            <StyledFilterSpacerBox/>
                            <StyledFilterBox>
                            <Button
                                id="run_financial_statements_button"
                                text="Apply"
                                color="primary"
                                size="small"
                                disabled={!validFilters}                
                                onClick={handleRunFinancialStatementsButtonClick}
                            />
                            </StyledFilterBox>
                        </FinancialStatementsActionsContainer>
                        ) : ('')}
                    </HorizontalBox>
                    {!runFinancialStatementsView && !validFilters ? '' : fsViewComponent}
                    {
                        !noDataMessage || soiView ? 
                            ""
                        :
                            <NoDataMessageTypography color={invalidSelectedLedgers?'Red' : ''}>{noDataMessage}</NoDataMessageTypography>
                    }
                </DataWrapperBox>
                <ReportDetails
                    open={!!fsDetailsApiParams}
                    reportParams={fsDetailsApiParams}
                    onClose={handleCloseReportDetails}
                    reportView={financialStatementsView}
                    summaryRowClicked={summaryRowClicked}
                    zeroSumStartBalance={zeroSumStartBalance}
                    level1Clicked={level1Clicked}
                    dateRange={dateRange}
                    netRow={netRow}
                    retainedRow={retainedRow}
                />
                <ConfirmationDialog
                    open={confirmNameChangeDialog}
                    onClose={() => setConfirmNameChangeDialog(false)}
                    id="fs_name_change_confirmation"
                    actions={[
                    {
                        label: 'Continue',
                        onClick: () => confirmFsNamechange(),
                        id: 'btn_save_fs_name_change',
                        variant: 'contained',
                        color: 'primary',
                    },
                    {
                        label: 'Cancel',
                        onClick: () => setConfirmNameChangeDialog(false),
                        id: 'btn_account_cancel_save',
                        variant: 'outlined',
                        color: 'error',
                    },
                    ]}
                    content="When updating the FS Display Name, this change will be reflected across all Accounts using the same FS Mapping within the fund(s) selected."
                    title="FS Display Name Change"
                />
            </MainContainer>
        </>
    );
};